<template>
  <div class="flex h-full flex-col gap-2">
    <AddChargingStationToOrganisationModal
      v-if="addModalVisible"
      :organisation-id="model.id"
      :columns="columns"
      @add-charging-stations="addChargingStations"
      @close="addModalVisible = false"
    />

    <CreateBatchJobModal
      v-if="showBatchModal"
      :station-selector="bulkSelector"
      :selection-count="bulkCount"
      :station-groups="bulkGroups"
      :batch-type="bulkBatchType"
      @created="onBatchCreated"
      @close="closeBatchModal"
    />

    <ChargingStationOverview
      :rows="chargingStations"
      :loading="isLoading"
      :page="currentPage"
      :search-enabled="true"
      :search-query="searchQuery"
      :has-more-items="hasMoreItems"
      :hide-pool-column="false"
      :columns="columns"
      :show-status-icons="showStatusIcons"
      :selection-chip-icon="['fal', 'charging-station']"
      context="start-action"
      :enable-selection="true"
      :total-matching="store.matchingTotal"
      :enable-bulk-actions="true"
      @click-row="redirectToChargingStation"
      @load="loadMore"
      @search="onSearch"
      @bulk-update="onBulkUpdate"
    >
      <template v-if="isDomainUser" #actions>
        <InfuseButton
          v-permission="{ id: chargingStationAccessManagementPermissions.ASSIGN, hide: false }"
          @click="addModalVisible = true"
        >
          <div class="flex items-center gap-2 font-normal whitespace-nowrap">
            <font-awesome-icon :icon="['fas', 'plus']" />
            {{ t('iam.organisations.chargingStations.action.add.label') }}
          </div>
        </InfuseButton>
      </template>
    </ChargingStationOverview>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Organisation } from '@infuse/api/iam/organisation';
import { assignChargingStation, chargingStationAccessManagementPermissions } from '@infuse/api/cpms/operator';
import { BatchJobType, type StationSelector } from '@infuse/api/cpms/batch';
import { ChargingStation, type StationGroup, useChargingStationStore } from '@infuse/cpms/charging-station/store';
import { type BulkUpdatePayload, ChargingStationOverview } from '@infuse/cpms/charging-station/ui';
import { CreateBatchJobModal } from '@infuse/cpms/batch/ui';
import { useNotify } from '@infuse/cpms/ui';
import { AddChargingStationToOrganisationModal } from '@infuse/iam/organisation/ui';
import { useCurrentUserLevel } from '@infuse/iam/composables';
import { vPermission } from '@infuse/iam/permission/directives';
import { InfuseButton } from '@infuse/ui/components';
import type { UiTableColumn } from '@infuse/ui/domain';
import { getAxiosErrorMessage } from '@infuse/utils';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const { notifySuccess, notifyFailure } = useNotify();
const { isDomainUser } = useCurrentUserLevel();
const route = useRoute();
const router = useRouter();

interface Props {
  model: Organisation;
  columns?: UiTableColumn[];
  showStatusIcons?: boolean;
}
const props = defineProps<Props>();

const store = useChargingStationStore();
store.resetItems();
store.loading = true;
const addModalVisible = ref(false);
const showBatchModal = ref(false);
const bulkSelector = ref<StationSelector>();
const bulkCount = ref(0);
const bulkGroups = ref<StationGroup[]>([]);
const bulkBatchType = ref<BatchJobType>();

const chargingStations = computed(() => store.items);
const isLoading = computed(() => store.loading);
const currentPage = computed(() => store.options.page);
const searchQuery = computed(() => store.options.query || '');
const hasMoreItems = computed(() => !store.options.lastPage);

watch(
  () => route.params.id as string,
  (id, oldId) => {
    if (id && id !== oldId) {
      store.resetItems();
      store.loading = true;
    }
  }
);

watch(
  () => props.model?.id,
  (id) => {
    if (id) {
      store.options.rootId = id;
      // "select all N matching" needs the filtered total, which is opt-in per surface.
      store.options.includeTotal = true;
      store.resetItems();
      store.search();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  store.options.rootId = undefined;
  store.resetOptions();
});

function loadMore() {
  store.loadMore();
}

function onSearch(query: string) {
  store.options.query = query;
  store.search();
}

function onBulkUpdate(payload: BulkUpdatePayload) {
  bulkSelector.value = payload.selector;
  bulkCount.value = payload.count;
  bulkBatchType.value = payload.batchType;
  bulkGroups.value = payload.groups;
  showBatchModal.value = true;
}

function closeBatchModal() {
  showBatchModal.value = false;
  bulkSelector.value = undefined;
  bulkCount.value = 0;
  bulkGroups.value = [];
  bulkBatchType.value = undefined;
}

// CreateBatchJobModal creates the batch + notifies itself; just close and refresh.
function onBatchCreated() {
  closeBatchModal();
  store.search();
}

function redirectToChargingStation(row: ChargingStation) {
  router.push({ name: 'charging-stations.details', params: { id: row.id } });
}

const addChargingStations = async (stations: ChargingStation[]) => {
  if (!stations.length) return;

  const results = await Promise.allSettled(
    stations.map((cs) => assignChargingStation({ id: cs.id }, { rootId: props.model.id }))
  );

  const failed = results.filter((r) => r.status === 'rejected');
  const succeeded = results.filter((r) => r.status === 'fulfilled');

  if (failed.length === 0) {
    notifySuccess({
      title: t('iam.organisations.chargingStations.notify.add.success.title'),
      message: t('iam.organisations.chargingStations.notify.add.success.message')
    });
  } else if (succeeded.length > 0) {
    notifyFailure({
      title: t('iam.organisations.chargingStations.notify.add.partial.title'),
      message: t('iam.organisations.chargingStations.notify.add.partial.message', {
        reason: getAxiosErrorMessage((failed[0] as PromiseRejectedResult).reason, '')
      })
    });
  } else {
    notifyFailure({
      title: t('iam.organisations.chargingStations.notify.add.error.title'),
      message: t('iam.organisations.chargingStations.notify.add.error.message', {
        reason: getAxiosErrorMessage((failed[0] as PromiseRejectedResult).reason, '')
      })
    });
  }

  addModalVisible.value = false;
  store.search();
};
</script>
